/**
 * Registro centralizado de endpoints del microservicio de reembolsos (slfcmbk).
 * Elimina la duplicación de lógica de resolución de ambiente en cada función de api.js.
 *
 * Uso:
 *   import { getRefundEndpoints } from './refunds-registry';
 *   const endpoints = getRefundEndpoints();
 *   // endpoints.quoteRefund → 'https://amx-c-slfcmbk-de.amlab7.com/sb/quoterefund'
 */

const POD = 'slfcmbk';

/**
 * Resuelve el sufijo de ambiente basado en el hostname actual.
 * Centraliza la lógica que antes se repetía inline en cada función.
 *
 * @returns { string } Sufijo de ambiente: '-pd.aeromexico.com' | '-q.amlab7.com' | '-de.amlab7.com'
 */
const resolveEnvironmentSuffix = () => {
  const hostname = window.location.hostname;
  const isProd = /^((www|beta|alpha).)*(aeromexico)\.com$/gi.test(hostname);
  const isMexEnv = /^(www.)*(mex.amlab7)\.com$/gi.test(hostname);
  const isPreProd = /^(www.)*(rc.amlab7)\.com$/gi.test(hostname);

  if (isProd) return '-pd.aeromexico.com';
  if (isMexEnv || isPreProd) return '-q.amlab7.com';
  return '-de.amlab7.com';
};

/**
 * Definición de paths por endpoint.
 * Cada key es un alias lógico; el value es el path relativo al pod+ambiente.
 */
const REFUND_PATHS = {
  fulfillRefund: '/rest/sb/fulfillrefund',
  loginRefund: '/sb/login',
  quoteRefund: '/rest/sb/quoterefund',
  specialRefund: '/sb/specialrefund',
};

/**
 * Genera las URLs completas de los endpoints de reembolsos.
 * Incluye el protocolo y prefijo de microservicio (https://amx-c-).
 *
 * @returns { { fulfillRefund: string, loginRefund: string, quoteRefund: string, specialRefund: string } }
 */
export const getRefundEndpoints = () => {
  const envSuffix = resolveEnvironmentSuffix();
  const base = `https://amx-c-${ POD }${ envSuffix }`;

  return {
    fulfillRefund: `${ base }${ REFUND_PATHS.fulfillRefund }`,
    loginRefund: `${ base }${ REFUND_PATHS.loginRefund }`,
    quoteRefund: `${ base }${ REFUND_PATHS.quoteRefund }`,
    specialRefund: `${ base }${ REFUND_PATHS.specialRefund }`,
  };
};

/**
 * Expone el sufijo de ambiente para uso externo si se necesita
 * (por ejemplo, para construir URLs de otros pods con la misma lógica).
 */
export { resolveEnvironmentSuffix };
